// Deal Profile onboarding wizard (Phase 5C slice 6).
//
// Five-step sequential reveal that walks first-time users through the
// key underwriting-thesis fields. State is in-memory until the final
// step's "Save profile" button, which POSTs a complete profile and
// redirects to its editor. Every question is skippable; the model's
// column defaults cover anything left blank.

import {svc} from '../../request';

interface IOnboardingStep {
	title: string;
	description: string;
	render: (container: HTMLElement) => void;
}

const STEP_LABELS = [
	'Name & mode',
	'Acquisition',
	'Property',
	'Cost basis',
	'Targets',
];

export class DealProfileOnboardingView {
	private stepsEl: HTMLElement | null;
	private bodyEl: HTMLElement | null;
	private footerEl: HTMLElement | null;
	private currentStep: number = 0;
	// Profile draft accumulates through the wizard. Empty fields are
	// dropped before POST so the model defaults take over.
	private draft: INewDealProfile = {
		name: '',
		mode: 'reno',
	};
	private saving: boolean = false;

	constructor() {
		this.stepsEl = document.getElementById('id_lb-deal-profile-onboarding-steps');
		this.bodyEl = document.getElementById('id_lb-deal-profile-onboarding-body');
		this.footerEl = document.getElementById('id_lb-deal-profile-onboarding-footer');
		this.render();
	}

	private steps(): Array<IOnboardingStep> {
		return [
			{
				title: 'What\'s this profile for?',
				description: 'Name it something memorable. Pick the mode you do most — you can also pick "Either" if you do both renovations and ground-up builds.',
				render: (c) => this.renderNameAndMode(c),
			},
			{
				title: 'What acquisition price range fits your deals?',
				description: 'Used to filter discovery candidates. Leave blank for unconstrained.',
				render: (c) => this.renderPriceBand(c),
			},
			{
				title: 'Any property size or age criteria?',
				description: 'Sqft range and minimum year built. All optional.',
				render: (c) => this.renderPropertyCriteria(c),
			},
			{
				title: 'What\'s your typical cost basis?',
				description: 'Used to pre-fill new deals. You can always tweak these per deal.',
				render: (c) => this.renderCostBasis(c),
			},
			{
				title: 'What metrics make a deal a pass?',
				description: 'Target ROI and profit margin. Discovery uses these to grade and rank candidates.',
				render: (c) => this.renderTargets(c),
			},
		];
	}

	private render(): void {
		this.renderStepsNav();
		this.renderBody();
		this.renderFooter();
	}

	private renderStepsNav(): void {
		if (!this.stepsEl) {
			return;
		}
		this.stepsEl.textContent = '';
		const steps = this.steps();
		steps.forEach((_, i) => {
			const dot = document.createElement('span');
			dot.className = 'lb-deal-profile-onboarding__step';
			if (i === this.currentStep) {
				dot.classList.add('lb-deal-profile-onboarding__step--active');
			} else if (i < this.currentStep) {
				dot.classList.add('lb-deal-profile-onboarding__step--done');
			}
			dot.textContent = String(i + 1);
			dot.title = STEP_LABELS[i] || '';
			this.stepsEl!.appendChild(dot);
		});
	}

	private renderBody(): void {
		if (!this.bodyEl) {
			return;
		}
		this.bodyEl.textContent = '';
		const step = this.steps()[this.currentStep];
		const head = document.createElement('header');
		head.className = 'lb-deal-profile-onboarding__step-head';
		const h2 = document.createElement('h2');
		h2.textContent = step.title;
		head.appendChild(h2);
		const desc = document.createElement('p');
		desc.className = 'lb-deal-profile-onboarding__step-desc';
		desc.textContent = step.description;
		head.appendChild(desc);
		this.bodyEl.appendChild(head);
		const content = document.createElement('div');
		content.className = 'lb-deal-profile-onboarding__step-content';
		step.render(content);
		this.bodyEl.appendChild(content);
	}

	private renderFooter(): void {
		if (!this.footerEl) {
			return;
		}
		this.footerEl.textContent = '';
		const isLast = this.currentStep === this.steps().length - 1;
		const isFirst = this.currentStep === 0;

		if (!isFirst) {
			const back = this.button('Back', () => {
				this.currentStep -= 1;
				this.render();
			}, 'lb-deal-profile-onboarding__btn--ghost');
			this.footerEl.appendChild(back);
		}

		const spacer = document.createElement('span');
		spacer.className = 'lb-deal-profile-onboarding__spacer';
		this.footerEl.appendChild(spacer);

		if (!isLast) {
			const skip = this.button('Skip', () => {
				this.currentStep += 1;
				this.render();
			}, 'lb-deal-profile-onboarding__btn--ghost');
			this.footerEl.appendChild(skip);
			const next = this.button(this.currentStep === 0 ? 'Continue' : 'Next', () => {
				this.currentStep += 1;
				this.render();
			}, 'lb-deal-profile-onboarding__btn--primary');
			next.disabled = this.currentStep === 0 && !(this.draft.name || '').trim();
			this.footerEl.appendChild(next);
		} else {
			const save = this.button(this.saving ? 'Saving…' : 'Save profile', () => {
				void this.handleSave();
			}, 'lb-deal-profile-onboarding__btn--primary');
			save.disabled = this.saving || !(this.draft.name || '').trim();
			this.footerEl.appendChild(save);
		}
	}

	private button(label: string, onClick: () => void, extraClass: string = ''): HTMLButtonElement {
		const b = document.createElement('button');
		b.type = 'button';
		b.className = `lb-deal-profile-onboarding__btn ${extraClass}`.trim();
		b.textContent = label;
		b.addEventListener('click', onClick);
		return b;
	}

	// ─── Step renderers ───

	private renderNameAndMode(c: HTMLElement): void {
		const nameLabel = document.createElement('label');
		nameLabel.className = 'lb-deal-profile-onboarding__field';
		const nameSpan = document.createElement('span');
		nameSpan.className = 'lb-deal-profile-onboarding__field-label';
		nameSpan.textContent = 'Profile name';
		nameLabel.appendChild(nameSpan);
		const nameInput = document.createElement('input');
		nameInput.type = 'text';
		nameInput.className = 'lb-deal-profile-onboarding__input';
		nameInput.placeholder = 'e.g. Coastal flips, Inland builds';
		nameInput.value = this.draft.name || '';
		nameInput.addEventListener('input', () => {
			this.draft.name = nameInput.value;
			this.renderFooter();  // Next button enable state depends on name
		});
		nameLabel.appendChild(nameInput);
		c.appendChild(nameLabel);

		const modeWrap = document.createElement('div');
		modeWrap.className = 'lb-deal-profile-onboarding__mode-row';
		(['reno', 'build', 'either'] as DealProfileMode[]).forEach(mode => {
			const label = mode === 'reno' ? 'Renovation / Flip'
				: mode === 'build' ? 'New Build'
				: 'Either / Both';
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'lb-deal-profile-onboarding__mode-btn';
			if (this.draft.mode === mode) {
				btn.classList.add('lb-deal-profile-onboarding__mode-btn--active');
			}
			btn.textContent = label;
			btn.addEventListener('click', () => {
				this.draft.mode = mode;
				this.renderBody();
			});
			modeWrap.appendChild(btn);
		});
		c.appendChild(modeWrap);
	}

	private renderPriceBand(c: HTMLElement): void {
		c.appendChild(this.bandFields('Acquisition price', 'minPrice', 'maxPrice', '$', 'integer'));
		c.appendChild(this.bandFields('Lot size', 'minLotAcres', 'maxLotAcres', 'acres', 'decimal'));
	}

	private renderPropertyCriteria(c: HTMLElement): void {
		c.appendChild(this.bandFields('Sqft', 'minSqft', 'maxSqft', 'sqft', 'integer'));
		c.appendChild(this.singleField('Year built (minimum)', 'minYearBuilt', 'year', 'integer'));
		c.appendChild(this.singleField('Beds (minimum)', 'minBeds', 'beds', 'integer'));
		c.appendChild(this.singleField('Baths (minimum)', 'minBaths', 'baths', 'decimal'));
	}

	private renderCostBasis(c: HTMLElement): void {
		c.appendChild(this.singleField('Reno cost ($/sqft)', 'renoCostPerSqft', '$/sqft', 'decimal'));
		c.appendChild(this.singleField('Build cost ($/sqft)', 'buildCostPerSqft', '$/sqft', 'decimal'));
		c.appendChild(this.singleField('Contingency (%)', 'contingencyPct', '%', 'decimal'));
		c.appendChild(this.singleField('Holding (months)', 'holdingMonths', 'months', 'decimal'));
		c.appendChild(this.singleField('Leverage (%) — 0 for cash', 'leveragePct', '%', 'decimal'));
		c.appendChild(this.singleField('Finance rate (%)', 'financeRatePct', '%', 'decimal'));
	}

	private renderTargets(c: HTMLElement): void {
		c.appendChild(this.singleField('Target ROI (%)', 'targetRoiPct', '%', 'decimal'));
		c.appendChild(this.singleField('Target margin (%)', 'targetMarginPct', '%', 'decimal'));
		c.appendChild(this.singleField('Max LTC (%)', 'maxLtcPct', '%', 'decimal'));
		c.appendChild(this.singleField('Max LTV (%)', 'maxLtvPct', '%', 'decimal'));
	}

	// ─── Field builders ───

	private bandFields(
		label: string,
		minField: keyof INewDealProfile,
		maxField: keyof INewDealProfile,
		unit: string,
		kind: 'integer' | 'decimal',
	): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-profile-onboarding__band';
		const lab = document.createElement('div');
		lab.className = 'lb-deal-profile-onboarding__field-label';
		lab.textContent = `${label} (${unit})`;
		row.appendChild(lab);
		const inputs = document.createElement('div');
		inputs.className = 'lb-deal-profile-onboarding__band-inputs';
		inputs.appendChild(this.numberInput(minField, kind, 'min'));
		const sep = document.createElement('span');
		sep.className = 'lb-deal-profile-onboarding__band-sep';
		sep.textContent = '–';
		inputs.appendChild(sep);
		inputs.appendChild(this.numberInput(maxField, kind, 'max'));
		row.appendChild(inputs);
		return row;
	}

	private singleField(
		label: string,
		field: keyof INewDealProfile,
		_unit: string,
		kind: 'integer' | 'decimal',
	): HTMLElement {
		const row = document.createElement('label');
		row.className = 'lb-deal-profile-onboarding__field';
		const lab = document.createElement('span');
		lab.className = 'lb-deal-profile-onboarding__field-label';
		lab.textContent = label;
		row.appendChild(lab);
		row.appendChild(this.numberInput(field, kind, ''));
		return row;
	}

	private numberInput(
		field: keyof INewDealProfile,
		kind: 'integer' | 'decimal',
		placeholder: string = '',
	): HTMLInputElement {
		const input = document.createElement('input');
		input.type = 'number';
		input.className = 'lb-deal-profile-onboarding__input';
		input.placeholder = placeholder;
		input.step = kind === 'integer' ? '1' : 'any';
		const v = this.draft[field];
		input.value = v === null || v === undefined ? '' : String(v);
		input.addEventListener('input', () => {
			const raw = input.value.trim();
			const next: number | null = raw === '' ? null : Number(raw);
			if (next === null || Number.isFinite(next)) {
				(this.draft as any)[field] = next;
			}
		});
		return input;
	}

	// ─── Save ───

	private async handleSave(): Promise<void> {
		const name = (this.draft.name || '').trim();
		if (!name) {
			return;
		}
		this.saving = true;
		this.renderFooter();
		try {
			const payload: INewDealProfile = {...this.draft, name};
			const profile = await svc.dealProfile.create(payload);
			window.location.assign(`/deals/profiles/${encodeURIComponent(profile.slug)}/`);
		} catch {
			this.saving = false;
			this.renderFooter();
		}
	}
}
